import { useMemo } from 'react';
import type { ResolvedDashboardSetting } from '../utils/resolveDashboardSetting';
import { useDashboardSettingsMap } from '../hooks/useDashboardSettingsQuery';
import {
  GROUP_COMPARISONS_OUTLIER_THRESHOLD_NAME,
} from '../dashboardSettingNames';
import {
  resolveDashboardSettingFromMap,
  resolveDashboardSettingsFromMap,
} from '../utils/resolveDashboardSetting';

export function useResolvedDashboardSetting(name: string): ResolvedDashboardSetting {
  const { map } = useDashboardSettingsMap();
  return useMemo(() => resolveDashboardSettingFromMap(map, name), [map, name]);
}

export function useResolvedDashboardSettings(names: string[]): Record<string, ResolvedDashboardSetting> {
  const { map } = useDashboardSettingsMap();
  return useMemo(() => resolveDashboardSettingsFromMap(map, names), [map, names]);
}

export function useGroupComparisonsOutlierThresholdResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting(GROUP_COMPARISONS_OUTLIER_THRESHOLD_NAME);
}

export function useRiskTriageAnomalyHighThresholdResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('Risk Triage Anomaly Score High Threshold');
}

export function useRiskTriageAnomalyMediumThresholdResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('Risk Triage Anomaly Score Medium Threshold');
}

export function useRiskTriageVarianceHighlightThresholdResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('Risk Triage Variance Highlight Threshold');
}

export function useSchoolProfileCostOutlierRatioResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('School Profile Cost Outlier Ratio Threshold');
}

export function useSchoolProfileCompletenessGoodThresholdResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('School Profile Completeness Good Threshold');
}

export function useSchoolProfileCompletenessWarningThresholdResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('School Profile Completeness Warning Threshold');
}

export function useBudgetLawAbMinimumPercentResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('Budget Law A+B Minimum Percent');
}

export function useBudgetLawAbWarningBandPercentResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('Budget Law A+B Warning Band Percent');
}

export function useTeacherEquitySpreadSeverityVeryHighRatioResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('Teacher Equity Spread Severity Very High Ratio');
}

export function useTeacherEquitySpreadSeverityHighRatioResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('Teacher Equity Spread Severity High Ratio');
}

export function useTeacherEquitySpreadSeverityVeryHighAmountResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('Teacher Equity Spread Severity Very High Amount USD');
}

export function useTeacherEquitySpreadSeverityHighAmountResolved(): ResolvedDashboardSetting {
  return useResolvedDashboardSetting('Teacher Equity Spread Severity High Amount USD');
}

export type { ResolvedDashboardSetting };
